import React from 'react';
import { Modal } from 'antd';
import { NotificationManager } from 'react-notifications';
import { isMobile } from 'react-device-detect';

const operationConfig = {
    RECALCULATE: { title: 'Chưa thể tính lại hoa hồng', problem: 'Dữ liệu hoa hồng chưa được tính lại.' },
    RECONCILE: { title: 'Chưa thể xác nhận đối soát', problem: 'Khoản hoa hồng chưa thể hoàn thành bước kiểm tra số liệu.' },
    APPROVE: { title: 'Chưa thể duyệt hoa hồng', problem: 'Khoản hoa hồng chưa đủ điều kiện để ghi nhận quyết định duyệt.' },
    LOCK_PERIOD: { title: 'Chưa thể khóa kỳ hoa hồng', problem: 'Kỳ hoa hồng vẫn còn nội dung cần xử lý trước khi khóa.' },
    FINALIZE_AGGREGATE: { title: 'Chưa thể chốt số liệu tổng hợp', problem: 'Số liệu tổng hợp của kỳ chưa thể chuyển sang trạng thái hoàn tất.' },
    RECORD_PAYOUT: { title: 'Chưa thể ghi nhận chi trả', problem: 'Kết quả giao dịch chưa được ghi nhận vào khoản chi trả.' },
    CANCEL_PAYOUT: { title: 'Chưa thể hủy danh sách chi trả', problem: 'Danh sách chi trả chưa đáp ứng điều kiện để hủy.' },
    PAYOUT_CORRECTION: { title: 'Chưa thể điều chỉnh giao dịch chi trả', problem: 'Yêu cầu điều chỉnh giao dịch chưa được ghi nhận.' },
    PAYOUT_SCHEDULE: { title: 'Chưa thể xử lý lịch chi trả', problem: 'Lịch chi trả chưa được tạo hoặc cập nhật trạng thái.' },
    ADJUSTMENT: { title: 'Chưa thể điều chỉnh hoa hồng', problem: 'Yêu cầu điều chỉnh hoa hồng chưa được ghi nhận.' },
    RECOVERY: { title: 'Chưa thể khấu trừ công nợ', problem: 'Yêu cầu khấu trừ công nợ hoa hồng chưa được ghi nhận.' },
    REFUND: { title: 'Chưa thể đảo nguồn tính hoa hồng', problem: 'Yêu cầu hoàn hoặc đảo nguồn chưa được ghi nhận.' },
    CARE_OUTCOME: { title: 'Chưa thể ghi nhận kết quả liên hệ', problem: 'Kết quả chăm sóc khách hàng chưa được ghi nhận.' },
    POLICY: { title: 'Chưa thể xử lý chính sách hoa hồng', problem: 'Chính sách chưa đáp ứng điều kiện của thao tác.' },
    POLICY_ACTIVATION: { title: 'Chính sách chưa thể kích hoạt', problem: 'Cấu hình chính sách còn nội dung chưa đạt yêu cầu.' },
};

let activeBusinessModal = null;

const formatEvidenceValue = value => {
    if (!value) return 'không có dữ liệu';
    const state = value.is_satisfied ? 'đạt' : 'chưa đạt';
    const evidence = value.evidence_count ? `${value.evidence_count} evidence` : 'không có evidence';
    const occurredAt = value.occurred_at ? `lúc ${value.occurred_at}` : 'chưa có thời điểm';
    return `${state}, ${evidence}, ${occurredAt}`;
};

export const formatCommissionErrorItem = item => {
    if (typeof item === 'string') return item;
    if (!item) return 'Điều kiện chưa đạt.';

    const failureDetails = item.details?.failures || [];
    const failureText = failureDetails.map(failure => (
        `${failure.condition_name || failure.condition_code} [${failure.reason_code}]: ` +
        `snapshot ${formatEvidenceValue(failure.snapshot)}; hiện tại ${formatEvidenceValue(failure.current)}; ` +
        `mốc khoản hoa hồng ${failure.item_reference_at || 'không xác định'}`
    )).join(' | ');
    const code = item.code ? `[${item.code}] ` : '';
    const identity = item.item_id ? `Khoản #${item.item_id}: ` : '';
    const message = item.message || item.reason || 'Điều kiện chưa đạt.';
    const action = item.action ? ` Cách xử lý: ${item.action}` : '';
    return `${identity}${code}${message}${failureText ? ` Chi tiết: ${failureText}.` : ''}${action}`;
};

const normalizeItems = value => {
    if (Array.isArray(value)) return value.filter(Boolean).map(item => {
        return formatCommissionErrorItem(item);
    });
    if (!value) return [];
    return String(value).split(';').map(item => item.trim()).filter(Boolean);
};

const Section = ({ title, children, items, tone = 'default' }) => {
    const colors = tone === 'danger'
        ? { background: '#fff2f0', border: '#ffccc7' }
        : tone === 'action'
            ? { background: '#f6ffed', border: '#b7eb8f' }
            : { background: '#fafafa', border: '#f0f0f0' };

    return (
        <div style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: 8, marginTop: 12, padding: '12px 14px', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
            {children}
            {!!items?.length && <ul style={{ margin: 0, paddingLeft: 20 }}>{items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>}
        </div>
    );
};

export const showCommissionBusinessError = ({
    operation = 'POLICY',
    problem,
    causes = [],
    nextActions = [],
    title,
}) => {
    const config = operationConfig[operation] || operationConfig.POLICY;

    if (activeBusinessModal) activeBusinessModal.destroy();
    activeBusinessModal = Modal.error({
        title: title || config.title,
        width: isMobile ? '100%' : '58%',
        maskClosable: false,
        okText: 'Đã hiểu',
        afterClose: () => { activeBusinessModal = null; },
        content: (
            <div style={{ marginTop: 8 }}>
                <Section title="Mô tả vấn đề">{problem || config.problem}</Section>
                <Section title="Nguyên nhân / điều kiện chưa thỏa" items={normalizeItems(causes)} tone="danger" />
                <Section title="Hướng xử lý / bước tiếp theo" items={normalizeItems(nextActions)} tone="action" />
            </div>
        ),
    });
};

const buildSystemMessage = (detail, fallback) => {
    if (detail?.category === 'AUTHORIZATION' && detail.cause) return detail.cause;
    return fallback;
};

/**
 * Command nghiệp vụ dùng Modal; lỗi tải dữ liệu, network và lỗi hệ thống giữ notification.
 */
export const presentCommissionError = (error, fallback, options = {}) => {
    const detail = error?.response?.data?.data?.error;
    const isBusinessCommand = options.presentation !== 'notification'
        && detail?.category === 'BUSINESS';

    if (isBusinessCommand) {
        // [ADJUSTMENT-RECOVERABLE-LIMIT] Hiển thị số tiền cụ thể thay vì chỉ báo xung đột chung.
        const adjustmentLimitCauses = detail?.code === 'ADJUSTMENT_EXCEEDS_AVAILABLE'
            ? [
                `Số tiền yêu cầu: ${Number(detail.details?.requested_amount || 0).toLocaleString('vi-VN')}đ`,
                `Có thể xử lý tối đa: ${Number(detail.details?.maximum_amount || 0).toLocaleString('vi-VN')}đ`,
                `Phần vượt: ${Number(detail.details?.excess_amount || 0).toLocaleString('vi-VN')}đ`,
            ]
            : null;
        showCommissionBusinessError({
            operation: options.operation,
            title: detail.step ? `${operationConfig[options.operation]?.title || 'Thao tác chưa hoàn tất'} — ${detail.step}` : undefined,
            problem: detail.message || detail.impact || options.problem,
            causes: adjustmentLimitCauses || detail.details?.checks || detail.details?.blockers || detail.reason || detail.cause,
            nextActions: [
                detail.code && detail.code !== 'ADJUSTMENT_EXCEEDS_AVAILABLE' ? `Mã lỗi: ${detail.code}` : null,
                detail.details?.item_id ? `Khoản hoa hồng: #${detail.details.item_id}` : null,
                detail.action || (detail.next_actions?.length ? detail.next_actions : detail.next_action),
            ].filter(Boolean),
        });
    } else {
        NotificationManager.error(buildSystemMessage(detail, fallback));
    }

    throw error;
};
